import * as React from 'react';
import { Button, Radio } from 'antd';
import { Link } from 'react-router-dom';
import posthog from 'posthog-js';
import { RadioChangeEvent } from 'antd/lib/radio';

import { POSTHOG_API_KEY, SENTRY_ENVIRONMENT } from 'config/env';
import { notArchivedFilterParam } from 'config/config';
import { FilterData } from 'common/helpers/url';
import { generateColumns } from 'common/helpers/utils';
import { SubmitButton } from 'common/components';
import { PaginatedTable } from 'features/pagination/components/PaginatedTable';
import { Contract, EditContractPaths } from 'features/contract/models/contractModels';
import { PaginationView, AutocompleteField } from 'features/pagination/models/paginationModel';
import { getMyContractsSuccess } from 'features/contract/actions/contractActions';
import { AutocompleteSelect } from 'features/pagination/components/AutocompleteSelect';
import { AssignActions } from 'features/benefit/models/benefitModels';
import { showNotification, notificationText } from 'common/helpers/notifications';
import {
    Antd3Form,
    Antd3Icon,
    Antd3FormProps,
    StyledAntd3Form,
} from 'common/components/deprecated/antd3';

import { EditPaths } from '../models/editModel';
import { EditMyContractsProps as EditMyContractsContainerProps } from '../containers/EditMyContractsContainer';
import { EditContractsTableFiltersBar } from './EditContractsTableFiltersBar';

if (SENTRY_ENVIRONMENT === 'frontend-production') {
    if (POSTHOG_API_KEY) {
        posthog.init(POSTHOG_API_KEY, {
            api_host: 'https://app.posthog.com',
            autocapture: false,
            capture_pageview: false,
        });
    }
}

interface EditMyContractsState {
    assign: AssignActions;
    selectedContractIds: string[];
}

const editContractColumns = [
    {
        title: 'Project',
        key: 'title',
        render: (title: string, { id }: Contract) => (
            <Link to={`${EditPaths.Contract}/${id}${EditContractPaths.ContractDetails}`}>
                {title}
            </Link>
        ),
    },
    {
        title: 'Ref. No.',
        key: 'referenceNumber',
    },
    {
        title: '',
        key: 'id',
        render: (_: string, { id }: Contract) => (
            <Link to={`${EditPaths.Contract}/${id}${EditContractPaths.ContractForm}`}>
                <Button size="small" icon={<Antd3Icon type="edit" />}>
                    Edit
                </Button>
            </Link>
        ),
        width: 70,
    },
];

type EditMyContractsProps = EditMyContractsContainerProps & Antd3FormProps;

class EditMyContractsForm extends React.Component<EditMyContractsProps, EditMyContractsState> {
    public state: EditMyContractsState = {
        assign: AssignActions.Assign,
        selectedContractIds: [],
    };

    private handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        this.props.form.validateFields((error, { userIds }) => {
            if (error) {
                return showNotification({ text: notificationText.Error });
            }
            return this.props.toggleMultipleEmployeesContractRoleRequest({
                contractIds: this.state.selectedContractIds.map((id) => parseInt(id, 10)),
                userIds: userIds.map((id: string) => parseInt(id, 10)),
                action: this.state.assign,
            });
        });
    };

    private refreshTable = (filters?: FilterData) =>
        this.props.paginationRequest({
            view: PaginationView.MyContracts,
            pagination: {
                current: 1,
            },
            filters,
            paginationSuccessCallback: getMyContractsSuccess,
        });

    private saveContractFilters = (contractTitle: string | undefined) =>
        this.props.saveFilters({
            paginatedView: PaginationView.MyContracts,
            values: {
                title: contractTitle,
            },
        });

    private searchByContracts = (contractTitle: string) =>
        this.saveContractFilters(contractTitle) && this.refreshTable();

    private onSelectChange = (selectedContractIds: string[]) =>
        this.setState({ selectedContractIds });

    private handleAssignChange = (event: RadioChangeEvent) =>
        this.setState({ assign: event.target.value });

    private onRemoveContractAutocomplete = () =>
        this.saveContractFilters(undefined) && this.refreshTable();

    private clearFilters = () =>
        this.props.clearFilters(PaginationView.MyContracts) &&
        this.refreshTable() &&
        this.props.form.setFieldsValue({
            title: '',
        });

    public render(): JSX.Element {
        const { myContracts, isUserAdmin, pageNumber, form } = this.props;
        const isContractUnselected = !this.state.selectedContractIds.length;
        const userIds = form.getFieldValue('userIds');
        const isEmployeeUnselected = !(userIds && userIds.length);

        return (
            <StyledAntd3Form onSubmit={this.handleSubmit} header="Edit My Projects" bigHeader>
                <EditContractsTableFiltersBar
                    searchByContractTitle={this.searchByContracts}
                    onRemoveContractTitleAutocomplete={this.onRemoveContractAutocomplete}
                    clearFilters={this.clearFilters}
                    form={this.props.form}
                    filter={this.props.filter}
                />
                <Antd3Form.Item>
                    <PaginatedTable
                        view={PaginationView.MyContracts}
                        paginationSuccessCallback={getMyContractsSuccess}
                        columns={generateColumns(editContractColumns)}
                        data={myContracts}
                        emptyText="No projects found"
                        pageNumber={pageNumber}
                        pathUrl={EditPaths.MyContracts}
                        rowSelection={
                            isUserAdmin
                                ? {
                                      selectedRowKeys: this.state.selectedContractIds,
                                      onChange: this.onSelectChange,
                                  }
                                : undefined
                        }
                        withPaginationHistory
                    />
                </Antd3Form.Item>

                {isUserAdmin && (
                    <>
                        <AutocompleteSelect
                            label="Select employees"
                            id="userIds"
                            form={form}
                            autocompleteField={AutocompleteField.Employee}
                            notFoundContent="No employees found"
                            multiple
                            required
                            requiredMessage="Please choose at least one employee"
                            searchParams={notArchivedFilterParam}
                        />
                        <Antd3Form.Item>
                            <Radio.Group
                                name="assign"
                                defaultValue={AssignActions.Assign}
                                onChange={this.handleAssignChange}
                            >
                                <Radio value={AssignActions.Assign}>Assign</Radio>
                                <Radio value={AssignActions.Revoke}>Unassign</Radio>
                            </Radio.Group>
                        </Antd3Form.Item>
                        <SubmitButton
                            value={
                                this.state.assign === AssignActions.Assign ? 'Assign' : 'Unassign'
                            }
                            disabled={isContractUnselected || isEmployeeUnselected}
                        />
                    </>
                )}
            </StyledAntd3Form>
        );
    }
}

export const EditMyContracts = Antd3Form.create({})(EditMyContractsForm);
